import React from 'react';
import styled from 'astroturf/react';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { useInBreakpoints } from 'js/design_system/hooks/useBreakpoint';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { trackClick } from 'js/utils/trackClick';
import { ReportList } from './ReportList';
import type { ReportListItem } from 'js/citeright/api';

type ReportListModalProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Modal wrapper for the ReportList component.
 * Displays a list of previous citation reports that users can click to view.
 * On mobile, the modal fills most of the screen.
 */
export const ReportListModal: React.FC<ReportListModalProps> = ({
  open,
  onClose,
}) => {
  const isMobile = useInBreakpoints(['small']);

  const handleSelectReport = (report: ReportListItem) => {
    trackClick({
      source: 'citeright.previous_report_click',
      entityId: report.id,
      extra: {
        status: report.status,
        file_type: report.file_type,
      },
    });
    // Navigate to the report page with the report_id query parameter
    window.location.href = `/citeright?report_id=${report.id}`;
  };

  return (
    <StyledModal
      open={open}
      onClose={onClose}
      title="Previous reports"
      containerStyle={
        isMobile
          ? {
              width: '100%',
              maxWidth: '100%',
              height: '90vh',
              maxHeight: '90vh',
              margin: 0,
              borderRadius:
                'var(--border-radius-radius-lg, 16px) var(--border-radius-radius-lg, 16px) 0 0',
            }
          : {
              width: '85vw',
              maxWidth: '1400px',
              maxHeight: '80vh',
            }
      }
    >
      <ImpressionWatcher
        source="citeright.previous_reports_modal"
        active={open}
      >
        <StyledModalContent>
          <ReportList onSelectReport={handleSelectReport} />
        </StyledModalContent>
      </ImpressionWatcher>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  /* Mobile: slide up from bottom */
  @media (max-width: 567px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const StyledModalContent = styled(ModalContent)`
  padding: 0;
  overflow: hidden;
`;

export default ReportListModal;
